import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { paths } from 'src/routes/paths';

import { clearCheckInRedirectGuards } from 'src/utils/check-in-redirect';
import { identifyUser, resetAnalyticsIdentity, trackSessionStartedOnce } from 'src/utils/analytics';

import { useUserStore } from 'src/store';
import { signOut, getLoggedInUserInfo } from 'src/api/auth';

export function useAuth() {
  const setUser = useUserStore((s) => s.setUser);
  const setLoading = useUserStore((s) => s.setLoading);

  const { data: user, isLoading } = useQuery({
    queryKey: ['user', 'my-info'],
    queryFn: getLoggedInUserInfo,
  });

  useEffect(() => {
    if (isLoading) return;
    setUser(user ?? null);
    setLoading(false);

    if (user) {
      identifyUser(user);
      trackSessionStartedOnce();
    }
  }, [user, isLoading, setUser, setLoading]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const setUser = useUserStore((s) => s.setUser);
  const navigate = useNavigate();

  const { mutate: logoutMutate } = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      queryClient.removeQueries();
      setUser(null);
      resetAnalyticsIdentity();
      clearCheckInRedirectGuards();
      navigate(paths.root);
    },
  });

  return { logout: () => logoutMutate() };
}
